import Breadcrumbs from "../../components/breadcrumb/breadcrumb";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import clsx from "clsx";
import { yupPasswordValidator } from "@myinnsworth/helpers/passwordHelpers";
import { authenticationHttpService } from "@myinnsworth/httpServices";
import Modal from "components/modal/modal";
import { useState } from "react";
import axios, { AxiosError } from "axios";

const changePasswordSchema = yup.object({
    password: yup.string().required('You must enter your current password'),
    newPassword: yupPasswordValidator,
    confirmNewPassword: yup.string()
        .required('You must confirm your new password')
        .oneOf([yup.ref('newPassword')], 'New passwords do not match')
}).required();

interface IChangePasswordSchema {
    password: string;
    newPassword: string;
    confirmNewPassword: string;
}

enum ChangePasswordState {
    None,
    Success,
    InProgress
}

export default function AccountSetting() {
    const [state, setState] = useState<ChangePasswordState>(ChangePasswordState.None);

    const breadcrumbs = [
        { label: 'Dashboard', url: '/' },
        { label: 'Account Settings', url: '/settings/account' },
    ];

    const { register: registerInput, handleSubmit, reset, clearErrors, setError, formState: { errors } } = useForm<IChangePasswordSchema>({
        resolver: yupResolver(changePasswordSchema)
    });

    const onCloseSuccess = () => {
        clearErrors();
        reset();
        setState(ChangePasswordState.None);
    };

    const onSubmit = async (formData: IChangePasswordSchema) => {
        clearErrors();
        try {
            setState(ChangePasswordState.InProgress);
            await authenticationHttpService.changePassword(formData);
            setState(ChangePasswordState.Success);
        } catch(ex) {
            if(axios.isAxiosError(ex)) {
                const error = ex as AxiosError;
                switch(error.response!.status) {
                    case 400: { //400 - Incorrect password
                        setError('password', { message: 'Incorrect password'});
                        break;
                    }
                    case 406: { //406 - Passwords do not match
                        setError('confirmNewPassword', { message: 'Passwords do not match'});
                        break;
                    }
                    case 417: { //417 - Password complexity
                        setError('password', { message: 'Password does not meet complexity rules'});
                        break;
                    }
                    default: {
                        console.error(ex);
                        break;
                    }
                }
            }
            setState(ChangePasswordState.None);
        }
    };

    return (
        <>
            <Breadcrumbs items={breadcrumbs} />

            <div className="spacing-m"></div>

            <label className="normal">Change password</label>

            <div className="spacing-m"></div>

            <section className="form-container">

                <form onSubmit={handleSubmit(onSubmit)} noValidate>

                    <div className="grid">

                        <div className={clsx('form-control', {'error': errors.password})}>
                            <label> Current password</label>
                            <input {...registerInput('password')} type="password"></input>
                            {errors.password && (<p>{errors.password.message}</p>)}
                            {!errors.password && (<p>Please enter your current password</p>)}
                        </div>

                        <div className={clsx('form-control', {'error': errors.newPassword})}>
                            <label> New password</label>
                            <input {...registerInput('newPassword')} type="password"></input>
                            {errors.newPassword && (<p>{errors.newPassword.message}</p>)}
                            {!errors.newPassword && (<p>Enter a new password</p>)}
                        </div>

                        <div className={clsx('form-control', {'error': errors.confirmNewPassword})}>
                            <label> Confirm new password</label>
                            <input {...registerInput('confirmNewPassword')} type="password"></input>
                            {errors.confirmNewPassword && (<p>{errors.confirmNewPassword.message}</p>)}
                            {!errors.confirmNewPassword && (<p>Enter your new password again</p>)}
                        </div>

                    </div>

                    <div className="flex justify-right pt-m">
                        <button title="Save" type="submit" className="btn primary">Save changes</button>
                    </div>
                </form>

            </section>
            
            {state === ChangePasswordState.Success && (
                <Modal 
                    title="Password changed"
                    children={<>
                        Your password was successfully changed
                        <div className="flex justify-right">
                            <button title="Close" className="btn primary medium" onClick={onCloseSuccess}>Close</button>
                        </div>
                    </>}
                    closeOnCoverClick={false}
                    onClose={onCloseSuccess} />
            )}
        </>
    )
}